import { useState, useEffect } from 'react';

export interface DeviceInfo {
  // Устройство
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isWindows: boolean;
  isMac: boolean;
  isLinux: boolean;
  isIPad: boolean; // Добавляем определение iPad
  
  // Детальная информация об iPad
  iPadInfo?: {
    model: string;
    screenSize: string;
    aspectRatio: string;
    isIPadOS: boolean;
  };
  
  // Браузер
  browser: string;
  browserVersion: string;
  isChrome: boolean;
  isFirefox: boolean;
  isSafari: boolean;
  isEdge: boolean;
  isOpera: boolean;
  isIE: boolean;
  
  // Дополнительная информация
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  pixelRatio: number;
  isTouchDevice: boolean;
  isLandscape: boolean;
  isPortrait: boolean;
  
  // PWA и режим отображения
  isPWA: boolean;
  isStandalone: boolean;
  isInAppBrowser: boolean;
  displayMode: 'standalone' | 'fullscreen' | 'minimal-ui' | 'browser';
}

const getBrowserInfo = (userAgent: string) => {
  const ua = userAgent.toLowerCase();
  
  // Определение браузера
  let browser = 'Unknown';
  let browserVersion = '';
  let isChrome = false;
  let isFirefox = false;
  let isSafari = false;
  let isEdge = false;
  let isOpera = false;
  let isIE = false;

  if (ua.includes('chrome') && !ua.includes('edg')) {
    browser = 'Chrome';
    isChrome = true;
    const match = ua.match(/chrome\/(\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (ua.includes('firefox')) {
    browser = 'Firefox';
    isFirefox = true;
    const match = ua.match(/firefox\/(\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (ua.includes('safari') && !ua.includes('chrome')) {
    browser = 'Safari';
    isSafari = true;
    const match = ua.match(/version\/(\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (ua.includes('edg')) {
    browser = 'Edge';
    isEdge = true;
    const match = ua.match(/edg\/(\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (ua.includes('opera') || ua.includes('opr')) {
    browser = 'Opera';
    isOpera = true;
    const match = ua.match(/(?:opera|opr)\/(\d+)/);
    browserVersion = match ? match[1] : '';
  } else if (ua.includes('msie') || ua.includes('trident')) {
    browser = 'Internet Explorer';
    isIE = true;
    const match = ua.match(/(?:msie|rv):(\d+)/);
    browserVersion = match ? match[1] : '';
  }

  return {
    browser,
    browserVersion,
    isChrome,
    isFirefox,
    isSafari,
    isEdge,
    isOpera,
    isIE
  };
};

const getDeviceInfo = (userAgent: string) => {
  const ua = userAgent.toLowerCase();
  
  // Определение операционной системы
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isAndroid = /android/.test(ua);
  const isWindows = /windows/.test(ua);
  const isMac = /macintosh|mac os x/.test(ua);
  const isLinux = /linux/.test(ua) && !isAndroid;
  
  // Функция для определения iPad
  const detectIPad = (): boolean => {
    // Способ 1: По user agent (самый надежный)
    if (/ipad/.test(ua)) {
      return true;
    }
    
    // Способ 2: По размеру экрана и touch capabilities
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // iPad имеет определенные размеры экрана
    const isIPadScreenSize = (
      (screenWidth === 768 && screenHeight === 1024) ||   // iPad 9.7"
      (screenWidth === 834 && screenHeight === 1112) ||   // iPad Pro 10.5"
      (screenWidth === 810 && screenHeight === 1080) ||   // iPad Air 10.9"
      (screenWidth === 820 && screenHeight === 1180) ||   // iPad Air 10.9"
      (screenWidth === 834 && screenHeight === 1194) ||   // iPad Pro 11"
      (screenWidth === 1024 && screenHeight === 1366) ||  // iPad Pro 12.9"
      (screenWidth === 834 && screenHeight === 1194) ||   // iPad Pro 11"
      (screenWidth === 1024 && screenHeight === 1366)     // iPad Pro 12.9"
    );
    
    // Проверяем viewport размеры (с учетом масштабирования)
    const isLargeViewport = viewportWidth >= 768 && viewportHeight >= 1024;
    
    // Способ 3: По CSS медиа-запросам
    const isTouchDevice = window.matchMedia && 
      window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  
    
    // Комбинированная проверка
    if (isIOS && !/iphone|ipod/.test(ua)) {
      // Если это iOS, но не iPhone/iPod
      if (hasTouch && (isIPadScreenSize || isLargeViewport)) {
        return true;
      }
    }
    
    // Дополнительная проверка для iPad с iPadOS 13+ (которые маскируются под Mac)
    if (isMac && hasTouch && isLargeViewport && isTouchDevice) {
      return true;
    }
    
    return false;
  };
  
  const isIPad = detectIPad();
  
  // Функция для получения детальной информации об iPad
  const getIPadInfo = (): DeviceInfo['iPadInfo'] | undefined => {
    if (!isIPad) return undefined;
    
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const aspectRatio = (screenWidth / screenHeight).toFixed(2);
    
    // Определяем модель iPad по размерам экрана
    let model = 'Unknown iPad';
    if (screenWidth === 768 && screenHeight === 1024) {
      model = 'iPad 9.7" (2010-2019)';
    } else if (screenWidth === 834 && screenHeight === 1112) {
      model = 'iPad Pro 10.5"';
    } else if (screenWidth === 810 && screenHeight === 1080) {
      model = 'iPad Air 10.9" (4th gen)';
    } else if (screenWidth === 820 && screenHeight === 1180) {
      model = 'iPad Air 10.9" (5th gen)';
    } else if (screenWidth === 834 && screenHeight === 1194) {
      model = 'iPad Pro 11"';
    } else if (screenWidth === 1024 && screenHeight === 1366) {
      model = 'iPad Pro 12.9"';
    }
    
    // Определяем, является ли это iPadOS (iOS 13+)
    const isIPadOS = parseInt(navigator.userAgent.match(/OS (\d+)_/)?.[1] || '0') >= 13;
    
    return {
      model,
      screenSize: `${screenWidth}x${screenHeight}`,
      aspectRatio,
      isIPadOS
    };
  };
  
  const iPadInfo = getIPadInfo();
  
  // Определение типа устройства
  const isMobile = /mobile|android|iphone|ipad|ipod|blackberry|windows phone/.test(ua);
  const isTablet = isIPad || /tablet/.test(ua) || (isMobile && window.innerWidth >= 768);
  const isDesktop = !isMobile && !isTablet;
  
  return {
    isMobile,
    isTablet,
    isDesktop,
    isIOS,
    isAndroid,
    isWindows,
    isMac,
    isLinux,
    isIPad,
    iPadInfo
  };
};

const getScreenInfo = () => {
  return {
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    pixelRatio: window.devicePixelRatio || 1,
    isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    isLandscape: window.innerWidth > window.innerHeight,
    isPortrait: window.innerHeight > window.innerWidth
  };
};

const getPWAInfo = () => {
  // Проверяем, запущено ли приложение в standalone режиме (добавлено на главный экран)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                      (window.navigator as any).standalone === true;
  
  // Определяем режим отображения
  let displayMode: 'standalone' | 'fullscreen' | 'minimal-ui' | 'browser' = 'browser';
  
  if (isStandalone) {
    displayMode = 'standalone';
  } else if (window.matchMedia('(display-mode: fullscreen)').matches) {
    displayMode = 'fullscreen';
  } else if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    displayMode = 'minimal-ui';
  }
  
  // Проверяем, является ли это PWA (есть service worker или manifest)
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasManifest = document.querySelector('link[rel="manifest"]') !== null;
  const isPWA = hasServiceWorker || hasManifest;
  
  // Проверяем, открыто ли в in-app браузере (например, в приложениях соцсетей)
  const isInAppBrowser = /FBAN|FBAV|Instagram|Line|WhatsApp|Telegram|Twitter|LinkedIn|Facebook|Snapchat|Pinterest|TikTok/i.test(navigator.userAgent);
  
  return {
    isPWA,
    isStandalone,
    isInAppBrowser,
    displayMode
  };
};

export const useDeviceInfo = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(() => {
    const userAgent = navigator.userAgent;
    const browserInfo = getBrowserInfo(userAgent);
    const deviceInfo = getDeviceInfo(userAgent);
    const screenInfo = getScreenInfo();
    const pwaInfo = getPWAInfo();
    
    return {
      ...browserInfo,
      ...deviceInfo,
      ...screenInfo,
      ...pwaInfo,
      userAgent
    };
  });

  useEffect(() => {
    const updateDeviceInfo = () => {
      const userAgent = navigator.userAgent;
      const browserInfo = getBrowserInfo(userAgent);
      const deviceInfo = getDeviceInfo(userAgent);
      const screenInfo = getScreenInfo();
      const pwaInfo = getPWAInfo();
      
      setDeviceInfo({
        ...browserInfo,
        ...deviceInfo,
        ...screenInfo,
        ...pwaInfo,
        userAgent
      });
    };

    // Обновляем информацию при изменении размера окна
    window.addEventListener('resize', updateDeviceInfo);
    window.addEventListener('orientationchange', updateDeviceInfo);

    return () => {
      window.removeEventListener('resize', updateDeviceInfo);
      window.removeEventListener('orientationchange', updateDeviceInfo);
    };
  }, []);

  return deviceInfo;
}; 